import { useState } from 'react'
import { Head, router, usePage } from '@inertiajs/react'

interface Props {
  invitation: {
    email: string
    organization_name: string
    invited_by_name: string
    role: string
  }
  token: string
  has_existing_account: boolean
  existing_account_name: string | null
  already_signed_in: boolean
}

export default function AcceptInvitation() {
  const { invitation, token, has_existing_account, existing_account_name, already_signed_in } = usePage().props as unknown as Props
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    password: '',
    password_confirmation: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const needsPassword = has_existing_account && !already_signed_in
  const isNewAccount = !has_existing_account

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (isNewAccount) {
      if (form.password !== form.password_confirmation) {
        setErrors({ password_confirmation: 'Passwords do not match' })
        return
      }
      if (form.password.length < 6) {
        setErrors({ password: 'Password must be at least 6 characters' })
        return
      }
    }

    if (needsPassword && form.password.length === 0) {
      setErrors({ password: 'Please enter your existing account password' })
      return
    }

    setSubmitting(true)
    setErrors({})

    let postData: Record<string, string> = {}
    if (isNewAccount) {
      postData = form
    } else if (needsPassword) {
      postData = { password: form.password }
    }

    router.post(`/invitations/${token}/complete`, postData, {
      onFinish: () => setSubmitting(false),
      onError: (errs) => setErrors(errs),
    })
  }

  return (
    <>
      <Head title="Accept Invitation" />
      <div className="min-h-screen bg-stone/10 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <img src="/pinnacle-logo.png" alt="Pinnacle" className="w-14 h-14 rounded-full mx-auto mb-3" />
            <h1 className="text-3xl font-bold text-text-primary font-heading">Join {invitation.organization_name}</h1>
            <p className="text-base text-text-secondary mt-1">
              {invitation.invited_by_name} invited you to join as {invitation.role.replace('_', ' ')}
            </p>
          </div>

          <div className="bg-white rounded-xl border border-stone/20 shadow-sm p-6">
            {has_existing_account ? (
              <form onSubmit={handleSubmit}>
                <div className="mb-4 p-4 rounded-lg bg-charcoal/5 border border-charcoal/20">
                  <p className="text-base text-text-primary font-medium">
                    Welcome back, {existing_account_name}!
                  </p>
                  <p className="text-sm text-text-muted mt-1">
                    {already_signed_in
                      ? 'You are already signed in. Click below to join this organization with your existing profile.'
                      : 'You already have an account with this email. Enter your password to verify your identity and join this organization.'
                    }
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-text-primary mb-1">Email</label>
                  <input
                    type="email"
                    value={invitation.email}
                    disabled
                    className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-secondary bg-stone/10"
                  />
                </div>

                {needsPassword && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-text-primary mb-1">Password</label>
                    <input
                      type="password"
                      value={form.password}
                      onChange={e => setForm(prev => ({ ...prev, password: e.target.value }))}
                      className={`w-full border rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal ${
                        errors.password ? 'border-pinnacle-red' : 'border-stone/30'
                      }`}
                      required
                      placeholder="Enter your existing account password"
                    />
                    {errors.password && <p className="text-sm text-pinnacle-red mt-1">{errors.password}</p>}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-pinnacle-red text-white text-base font-semibold rounded-lg hover:bg-pinnacle-red/90 transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Joining...' : `Join ${invitation.organization_name}`}
                </button>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Email</label>
                  <input
                    type="email"
                    value={invitation.email}
                    disabled
                    className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-secondary bg-stone/10"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">First name</label>
                    <input
                      type="text"
                      value={form.first_name}
                      onChange={e => setForm(prev => ({ ...prev, first_name: e.target.value }))}
                      className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">Last name</label>
                    <input
                      type="text"
                      value={form.last_name}
                      onChange={e => setForm(prev => ({ ...prev, last_name: e.target.value }))}
                      className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Password</label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={e => setForm(prev => ({ ...prev, password: e.target.value }))}
                    className={`w-full border rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal ${
                      errors.password ? 'border-pinnacle-red' : 'border-stone/30'
                    }`}
                    required
                    minLength={6}
                  />
                  {errors.password && <p className="text-sm text-pinnacle-red mt-1">{errors.password}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Confirm password</label>
                  <input
                    type="password"
                    value={form.password_confirmation}
                    onChange={e => setForm(prev => ({ ...prev, password_confirmation: e.target.value }))}
                    className={`w-full border rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal ${
                      errors.password_confirmation ? 'border-pinnacle-red' : 'border-stone/30'
                    }`}
                    required
                  />
                  {errors.password_confirmation && <p className="text-sm text-pinnacle-red mt-1">{errors.password_confirmation}</p>}
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-pinnacle-red text-white text-base font-semibold rounded-lg hover:bg-pinnacle-red/90 transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Creating account...' : 'Accept & Join'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
